/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from 'react';
import {
    Search,
    Check, X,
    MapPin, Phone, Mail, UserPlus, Calendar, FileText, Loader2, Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import RequestsHeader from '../section/RequestsHeader';
import { toast } from 'react-hot-toast';
import { canAccess } from '../../utils/permissions';
import { ClipboardCheck } from 'lucide-react';
import { generateJobNumber } from '../../utils/jobHelpers';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getInitials = (firstName, lastName) => {
    if (firstName && lastName) {
        return (firstName[0] + lastName[0]).toUpperCase();
    }
    if (firstName) {
        return firstName.substring(0, 2).toUpperCase();
    }
    return '?';
};

export default function Requests() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [, setShowStatusMenu] = useState(false);
    const [selectedStatus,] = useState('All');
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [, setSelectAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Assign Modal States
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedRequestForAssign, setSelectedRequestForAssign] = useState(null);
    const [jobNumber, setJobNumber] = useState('');
    const [totalVisits, setTotalVisits] = useState(1);
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [repeats, setRepeats] = useState('does-not-repeat');
    const [visitInstructions, setVisitInstructions] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedSurveyors, setSelectedSurveyors] = useState([]);

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);

    const canCreateJob = canAccess('create_job');

    const { data: requestsData, isLoading: isLoadingRequests, error: requestsError, refetch: refetchRequests } = useQuery({
        queryKey: ['acceptedOffers'],
        queryFn: async () => {
            const response = await api.get('/company/offers/accepted');
            return response.data.data || [];
        },
    });

    const { data: surveyorsData, isLoading: isLoadingSurveyors } = useQuery({
        queryKey: ['surveyors'],
        queryFn: async () => {
            const response = await api.get('/company/surveyors');
            return response.data.data || [];
        },
    });

    const requests = requestsData || [];
    const surveyors = surveyorsData || [];

    const createJobMutation = useMutation({
        mutationFn: async (payload) => {
            return await api.post('/company/jobs', payload);
        },
        onSuccess: () => {
            toast.success('Job created successfully');
            queryClient.invalidateQueries(['acceptedOffers']);
            queryClient.invalidateQueries(['companyJobs']);
            resetAssignModal();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create job');
        },
    });

    const deleteOfferMutation = useMutation({
        mutationFn: async (id) => {
            return await api.delete(`/company/offers/${id}/delete`);
        },
        onSuccess: () => {
            toast.success('Request deleted successfully');
            queryClient.invalidateQueries(['acceptedOffers']);
            setShowDeleteModal(false);
            setIdToDelete(null);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete request');
            setShowDeleteModal(false); // Optional: close on error or keep open to retry
        },
    });

    const handleDelete = (id, e) => {
        e.stopPropagation();
        setIdToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (idToDelete) {
            deleteOfferMutation.mutate(idToDelete);
        }
    };

    useEffect(() => {
        const handleClickOutside = () => {
            setShowStatusMenu(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const filteredRequests = useMemo(() => {
        let filtered = [...requests];

        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter((request) => {
                const clientName = `${request.quote?.first_name || ''} ${request.quote?.last_name || ''}`.toLowerCase();
                const email = (request.quote?.email || '').toLowerCase();
                const phone = (request.quote?.phone || '').toLowerCase();
                const property = (request.quote?.boat_location || '').toLowerCase();

                return clientName.includes(searchLower) ||
                    email.includes(searchLower) ||
                    phone.includes(searchLower) ||
                    property.includes(searchLower);
            });
        }

        return filtered;
    }, [requests, selectedStatus, searchTerm]);

    const statuses = useMemo(() => {
        requests.forEach(() => {
            // You can add status counting logic here if needed
        });
        return [
            { label: 'All', count: requests.length },
            { label: 'New', count: 0 },
            { label: 'Assessment complete', count: 0 },
            { label: 'Overdue', count: 0 },
            { label: 'Unscheduled', count: 0 }
        ];
    }, [requests]);

    if (!canAccess("view_quote")) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
                    <ClipboardCheck className="w-10 h-10 text-red-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
                <p className="text-gray-500 max-w-md font-medium">
                    You don't have the required permissions to view accepted requests.
                </p>
            </div>
        );
    }

    const handleRowClick = (id) => {
        navigate(`/profile/details/requests/${id}`);
    };

    const handleAssignClick = (request) => {
        setSelectedRequestForAssign(request);
        setShowAssignModal(true);
        const today = new Date().toISOString().split('T')[0];
        setJobNumber(generateJobNumber());
        setStartDate(today);
        setSelectedSurveyors([]);
    };

    const toggleSurveyor = (surveyorId) => {
        setSelectedSurveyors(prev => {
            const existing = prev.find(s => s.id === surveyorId);
            if (existing) {
                return prev.filter(s => s.id !== surveyorId);
            } else {
                return [...prev, { id: surveyorId, amount: 150.00 }];
            }
        });
    };

    const updateSurveyorAmount = (surveyorId, amount) => {
        setSelectedSurveyors(prev =>
            prev.map(s => s.id === surveyorId ? { ...s, amount: Number(amount) || 0 } : s)
        );
    };

    const resetAssignModal = () => {
        setShowAssignModal(false);
        setSelectedRequestForAssign(null);
        const today = new Date().toISOString().split('T')[0];
        setJobNumber('');
        setTotalVisits(1);
        setStartDate(today);
        setStartTime('');
        setEndTime('');
        setRepeats('does-not-repeat');
        setVisitInstructions('');
        setNotes('');
        setSelectedSurveyors([]);
    };

    const handleSaveJob = () => {
        if (!selectedRequestForAssign) return;

        // Validate required fields
        if (!jobNumber.trim()) {
            toast.error('Please enter a job number');
            return;
        }
        if (!startDate) {
            toast.error('Please select a start date');
            return;
        }
        if (selectedSurveyors.length === 0) {
            toast.error('Please select at least one surveyor');
            return;
        }

        const quoteId = selectedRequestForAssign.quote_id || selectedRequestForAssign.quote?.id;
        if (!quoteId) {
            toast.error('Quote ID not found');
            return;
        }

        const payload = {
            quote_id: quoteId,
            job_number: jobNumber,
            total_visits: totalVisits,
            start_date: startDate,
            start_time: startTime || null,
            end_time: endTime || null,
            repeats: repeats,
            visit_instructions: visitInstructions || null,
            notes: notes || null,
            surveyors: selectedSurveyors.map(s => ({
                id: s.id,
                amount: s.amount
            }))
        };

        createJobMutation.mutate(payload);
    };

    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return (
        <section className="min-h-screen text-sm">
            <div className="bg-white border-b border-gray-200 px-4 py-3">
                <RequestsHeader />

                {/* Overview Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow transition">
                        <p className="text-xs font-medium text-gray-600">Overview</p>
                        <ul className="mt-2 space-y-1 text-xs">
                            <li className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                                <span className="font-medium">New ({requests.length})</span>
                            </li>
                            {/* <li className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-green-600"></span>
                                <span className="font-medium">Assessment complete (0)</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                                <span className="font-medium">Overdue (0)</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                                <span className="font-medium">Unscheduled (0)</span>
                            </li> */}
                        </ul>
                    </div>

                    <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow transition">
                            <p className="text-xs font-medium text-gray-600">New requests</p>
                            <p className="text-xs text-gray-500">Total accepted offers</p>
                            <div className="flex items-end justify-between mt-2">
                                <p className="text-2xl font-bold text-gray-900">{requests.length}</p>
                            </div>
                        </div>

                        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow transition">
                            <p className="text-xs font-medium text-gray-600">Conversion rate</p>
                            <p className="text-xs text-gray-500">Accepted offers</p>
                            <div className="flex items-end justify-between mt-2">
                                <p className="text-2xl font-bold text-gray-900">100%</p>
                            </div>
                        </div> */}
                    </div>
                </div>
            </div>

            {/* Selected Bar */}
            {selectedRequests.length > 0 && (
                <div className="fixed top-16 left-1/2 -translate-x-1/2 z-40 bg-white rounded-xl shadow-xl border border-gray-200 px-4 py-2.5 flex items-center gap-4 text-xs animate-in fade-in slide-in-from-top">
                    <div className="flex items-center gap-2">
                        <button onClick={() => { setSelectedRequests([]); setSelectAll(false); }} className="p-1.5 hover:bg-gray-100 rounded">
                            <X className="w-4 h-4 text-gray-600" />
                        </button>
                        <span className="font-semibold">{selectedRequests.length} selected</span>
                    </div>
                    <div className="h-8 w-px bg-gray-300" />
                    <button className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium">
                        Change Status
                    </button>
                </div>
            )}

            {/* Table Section */}
            <div className="px-4 py-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">All requests</h2>
                        <p className="text-xs text-gray-600">({filteredRequests.length} result{filteredRequests.length !== 1 ? 's' : ''})</p>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Status Dropdown */}


                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-3 py-2 w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-xs bg-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {isLoadingRequests && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-emerald-600" />
                        <p className="text-gray-600 text-xs">Loading requests...</p>
                    </div>
                )}

                {/* Error State */}
                {requestsError && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                        <p className="text-red-600 mb-4 text-xs">Failed to load requests</p>
                        <button
                            onClick={() => refetchRequests()}
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-xs"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Table */}
                {!isLoadingRequests && !requestsError && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-xs">
                        {filteredRequests.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <p className="text-xs">No requests found</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-700">
                                    <tr>

                                        <th className="text-left px-3 py-3">Client</th>
                                        <th className="text-left px-3 py-3">Property</th>
                                        <th className="text-left px-3 py-3">Contact</th>
                                        <th className="text-left px-3 py-3">Requested</th>
                                        <th className="text-left px-3 py-3">Status</th>
                                        <th className="text-left px-3 py-3">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="text-xs">
                                    {filteredRequests.map((request) => {
                                        const quote = request.quote || {};
                                        const clientName = `${quote.first_name || ''} ${quote.last_name || ''}`.trim() || 'Unknown';
                                        const property = quote.boat_location || 'N/A';

                                        return (
                                            <tr
                                                key={request.id}
                                                className="border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer"
                                                onClick={() => handleRowClick(request.id)}
                                            >

                                                <td className="px-3 py-3">
                                                    <div className="font-medium text-gray-900">{clientName}</div>
                                                    {quote.email && (
                                                        <div className="text-xs text-gray-500">{quote.email}</div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <div className="flex items-start gap-1.5 text-gray-700">
                                                        <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                                        <span className="text-xs">{property}</span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <div className="space-y-0.5">
                                                        {quote.phone && (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-700">
                                                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                                {quote.phone}
                                                            </div>
                                                        )}
                                                        {quote.email && (
                                                            <div className="flex items-center gap-1.5 text-xs text-gray-700">
                                                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                                {quote.email}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3 text-gray-600 text-xs">
                                                    {formatDate(request.created_at)}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                                        Accepted
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center gap-2">
                                                        {canCreateJob && !request.has_job && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleAssignClick(request);
                                                                }}
                                                                className="px-2.5 py-1.5 bg-teal-50 text-teal-600 rounded-lg text-xs font-medium hover:bg-teal-100 transition flex items-center gap-1.5"
                                                            >
                                                                <UserPlus className="w-3.5 h-3.5" />
                                                                Assign
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => handleDelete(request.id, e)}
                                                            className="px-2.5 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition flex items-center gap-1.5"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {/* Assign Job Modal */}
            {showAssignModal && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-50" onClick={resetAssignModal} />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-4xl my-8">
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-5 rounded-t-xl">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
                                            <FileText className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-white">Assign Job</h2>
                                            <p className="text-xs text-white/80">Create and schedule a job</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-lg border border-white/30">
                                            <Calendar className="w-3.5 h-3.5 text-white" />
                                            <span className="text-xs font-medium text-white">{today}</span>
                                        </div>
                                        <button
                                            onClick={resetAssignModal}
                                            className="p-1.5 hover:bg-white/20 rounded-lg transition"
                                        >
                                            <X className="w-5 h-5 text-white" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="p-5 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
                                {/* Request Info */}
                                {selectedRequestForAssign && (
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1.5">Request</label>
                                                <div className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50">
                                                    <div className="font-medium text-gray-900">
                                                        Quote #{selectedRequestForAssign.quote_id || selectedRequestForAssign.quote?.id || 'N/A'}
                                                    </div>
                                                    {selectedRequestForAssign.quote && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {selectedRequestForAssign.quote.first_name} {selectedRequestForAssign.quote.last_name} • {selectedRequestForAssign.quote.boat_location || 'N/A'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1.5">Client</label>
                                                <div className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 flex items-center justify-between">
                                                    {selectedRequestForAssign.quote && (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-7 h-7 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
                                                                {getInitials(selectedRequestForAssign.quote.first_name, selectedRequestForAssign.quote.last_name)}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">
                                                                    {selectedRequestForAssign.quote.first_name} {selectedRequestForAssign.quote.last_name}
                                                                </p>
                                                                {selectedRequestForAssign.quote.phone && (
                                                                    <p className="text-xs text-gray-500">{selectedRequestForAssign.quote.phone}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1.5">Job # *</label>
                                                <input
                                                    type="text"
                                                    value={jobNumber}
                                                    readOnly
                                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed text-sm"
                                                    placeholder="Auto-generating..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Schedule */}
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                    <h3 className="text-sm font-bold text-gray-900 mb-4">Schedule</h3>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Total visits *</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={totalVisits}
                                                onChange={(e) => setTotalVisits(parseInt(e.target.value) || 1)}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                                            />
                                        </div>

                                        <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className="px-2 py-1 bg-emerald-600 text-white rounded text-xs font-medium">On</div>
                                                <span className="text-sm font-medium text-gray-900">
                                                    {startDate ? new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Select date'}
                                                </span>
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Start date *</label>
                                                    <input
                                                        type="date"
                                                        value={startDate}
                                                        onChange={(e) => setStartDate(e.target.value)}
                                                        className="w-full px-3 py-2 bg-white border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1.5">Start time</label>
                                                        <input
                                                            type="time"
                                                            value={startTime}
                                                            onChange={(e) => setStartTime(e.target.value)}
                                                            className="w-full px-3 py-2 bg-white border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1.5">End time</label>
                                                        <input
                                                            type="time"
                                                            value={endTime}
                                                            onChange={(e) => setEndTime(e.target.value)}
                                                            className="w-full px-3 py-2 bg-white border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Repeats</label>
                                            <select
                                                value={repeats}
                                                onChange={(e) => setRepeats(e.target.value)}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm bg-white"
                                            >
                                                <option value="does-not-repeat">Does not repeat</option>
                                                <option value="daily">Daily</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="monthly">Monthly</option>
                                                <option value="custom">Custom</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">Visit instructions</label>
                                            <textarea
                                                rows="3"
                                                value={visitInstructions}
                                                onChange={(e) => setVisitInstructions(e.target.value)}
                                                placeholder="Add instructions for the team..."
                                                className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Surveyors */}
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-bold text-gray-900">Surveyors *</h3>
                                        <p className="text-xs text-gray-500">Select surveyors and set amount for each</p>
                                    </div>

                                    {isLoadingSurveyors ? (
                                        <div className="text-center py-4">
                                            <Loader2 className="w-5 h-5 mx-auto animate-spin text-emerald-600" />
                                            <p className="text-xs text-gray-500 mt-2">Loading surveyors...</p>
                                        </div>
                                    ) : surveyors.length === 0 ? (
                                        <p className="text-sm text-gray-500">No surveyors available</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {surveyors.map((surveyor) => {
                                                const isSelected = selectedSurveyors.some(s => s.id === surveyor.id);
                                                const selectedSurveyor = selectedSurveyors.find(s => s.id === surveyor.id);

                                                return (
                                                    <div
                                                        key={surveyor.id}
                                                        className={`border-2 rounded-lg p-3 transition ${isSelected
                                                            ? 'border-emerald-500 bg-emerald-50'
                                                            : 'border-gray-200 hover:border-gray-300 bg-white'
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleSurveyor(surveyor.id)}
                                                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${isSelected
                                                                    ? 'border-emerald-600 bg-emerald-600'
                                                                    : 'border-gray-300'
                                                                    }`}
                                                            >
                                                                {isSelected && (
                                                                    <Check className="w-3 h-3 text-white" />
                                                                )}
                                                            </button>
                                                            <div className="flex-1">
                                                                <p className="text-sm font-medium text-gray-900">{surveyor.full_name}</p>
                                                                {surveyor.phone && (
                                                                    <p className="text-xs text-gray-500">{surveyor.phone}</p>
                                                                )}
                                                            </div>
                                                            {isSelected && (
                                                                <div className="w-32">
                                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        value={selectedSurveyor?.amount || 150.00}
                                                                        onChange={(e) => updateSurveyorAmount(surveyor.id, e.target.value)}
                                                                        className="w-full px-2 py-1.5 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm bg-white"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {selectedSurveyors.length === 0 && (
                                        <p className="text-xs text-gray-500 mt-3">Please select at least one surveyor</p>
                                    )}
                                </div>

                                {/* Notes */}
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                                    <h3 className="text-sm font-bold text-gray-900 mb-1">Notes</h3>
                                    <p className="text-xs text-gray-600 mb-3">Leave an internal note for yourself or a team member</p>
                                    <textarea
                                        rows="4"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Add your notes here..."
                                        className="w-full p-3 bg-gray-50 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-gray-400/20 focus:border-gray-400 text-sm"
                                    />
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="flex justify-end gap-2 p-5 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                                <button
                                    onClick={resetAssignModal}
                                    disabled={createJobMutation.isPending}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 transition disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveJob}
                                    disabled={createJobMutation.isPending}
                                    className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {createJobMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        'Save'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowDeleteModal(false)} />
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Trash2 className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Delete Request</h3>
                            <p className="text-sm text-gray-500 text-center mb-6">
                                Are you sure you want to delete this request? This action cannot be undone.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    disabled={deleteOfferMutation.isPending}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition flex items-center justify-center gap-2"
                                >
                                    {deleteOfferMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}